import { useDispatch, useSelector } from "react-redux";
import {
    getAcuantSDKDPIThreshold,
    getAcuantSDKGlareThreshold,
    getAcuantSDKSharpnessThreshold,
} from "@redux/selectors/verification-configuration-selectors";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { QualityCheck } from "./quality-check/quality-check";
import { ValidationTypes } from "@enums/validation-types";
import { MutableRefObject, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons/faCog";
import classNames from "classnames";
import { Logger } from "@utils/logger";
import { ValidationResult } from "@idw-types/common/validation-result";
import { performValidationStep } from "@utils/validation-util";
import { AcuantDocumentProcessingValidationResult } from "@idw-types/common/acuant-document-processing-result";
import { useAuditLogger } from "@hooks/use-audit-logger";
import styles from "./quality-checks-container.less";
import { setCaptureStepValidationResults } from "@redux/actions/validation-results-actions";
import { CaptureStep } from "@idw-types/data/capture-step";
import { getCaptureStepValidationResults } from "@redux/selectors/validation-results-selectors";
import { CaptureStepValidationResults } from "@idw-types/data/validation-results";
import { getInitialValidationResult } from "@utils/capture-step-validation-util";
import { APValidationLivenessAssessmentResult } from "@idw-types/requests/ap-validation-liveness-assessment-result";
import { setSharpnessFailureCount } from "@redux/actions/verification-step-config-actions";
import { getSharpnessFailureCount } from "@redux/selectors/verification-step-config-selectors";
import { MAX_BLUR_FAILURES_BEFORE_NATIVE_CAMERA } from "@constants/form-constants";

type QualityChecksContainerProps = {
    captureStep: CaptureStep
    imageRef: MutableRefObject<HTMLImageElement>;
    imageUrl: string;
    acuantValidationResults: AcuantDocumentProcessingValidationResult;
    livenessValidationResults: APValidationLivenessAssessmentResult;
    imagePreviewLoaded: boolean;
};

export const QualityChecksContainer = (props: QualityChecksContainerProps) => {
    const {
        captureStep,
        imageRef,
        imageUrl,
        acuantValidationResults,
        livenessValidationResults,
        imagePreviewLoaded
    } = props;

    const dispatch = useDispatch();
    const { writeAuditLog } = useAuditLogger();
    const [validationStartTime, setValidationStartTime] = useState(null);
    const [validationInitialized, setValidationInitialized] = useState(false);
    const [validationFinished, setValidationFinished] = useState(false);
    const content = useSelector(getLanguageContent);
    const sharpnessFailureCount = useSelector(getSharpnessFailureCount);
    const acuantSDKGlareThreshold = useSelector(getAcuantSDKGlareThreshold);
    const acuantSDKSharpnessThreshold = useSelector(getAcuantSDKSharpnessThreshold);
    const acuantSDKDPIThreshold = useSelector(getAcuantSDKDPIThreshold);
    //@ts-ignore
    const captureStepValidationResults = useSelector(state => getCaptureStepValidationResults(state, captureStep.type));

    const acuantThresholds = {
        glare: acuantSDKGlareThreshold,
        dpi: acuantSDKDPIThreshold,
        sharpness: acuantSDKSharpnessThreshold
    };

    const handleValidationStep = async (validationStep: ValidationResult) => {
        const result = await performValidationStep(
            validationStep.type,
            imageRef.current,
            livenessValidationResults,
            acuantValidationResults,
            acuantThresholds,
            writeAuditLog
        );

        if (validationStep.type === ValidationTypes.SHARPNESS_DETECTION && result.isValid === false) {
            const newSharpnessFailureCount = sharpnessFailureCount + 1;
            if (newSharpnessFailureCount === MAX_BLUR_FAILURES_BEFORE_NATIVE_CAMERA && typeof AcuantCamera !== "undefined") {
                AcuantCamera.isCameraSupported = false;
            }
            dispatch(setSharpnessFailureCount(newSharpnessFailureCount));
        }

        Logger.info(`[CaptureValidationComponent] ${validationStep.type} result: ${JSON.stringify(result)}`);
        return result;
    };

    const performValidation = async () => {
        setValidationStartTime(window.performance.now());

        try {
            const resultPromises: Array<Promise<ValidationResult>> = [];
            Object.keys(captureStepValidationResults).forEach((validationType: ValidationTypes) => {
                resultPromises.push(handleValidationStep(captureStepValidationResults[validationType]));
            });
            const results = await Promise.all(resultPromises);
            const newCaptureStepValidationResults: CaptureStepValidationResults = {};
            results.forEach(result => {
                newCaptureStepValidationResults[result.type] = result;
            });
            dispatch(setCaptureStepValidationResults(captureStep.type, newCaptureStepValidationResults));
            setValidationFinished(true);
        } catch (error) {
            Logger.info('[CaptureValidationComponent] *************** VALIDATION ERROR ***************');
            Logger.info('[CaptureValidationComponent] Error in validation: ', error);
            setValidationFinished(true);
        }
    };

    useEffect(() => {
        if (imageUrl && imagePreviewLoaded) {
            setValidationInitialized(false);
            setValidationFinished(false);
            const newCaptureStepValidationResults: CaptureStepValidationResults = {}
            Object.keys(captureStepValidationResults).forEach((validationType: ValidationTypes) => {
                newCaptureStepValidationResults[validationType] = {
                    ...getInitialValidationResult(validationType),
                    inProgress: true
                }
            });
            dispatch(setCaptureStepValidationResults(captureStep.type, newCaptureStepValidationResults));
            setTimeout(() => {
                Logger.info('[CaptureValidationComponent] *************** VALIDATION RUNNING ************');
                Logger.info(`[CaptureValidationComponent] validation steps: ${JSON.stringify(newCaptureStepValidationResults)}`);    
                setValidationInitialized(true);
            }, 100);
        }
    }, [acuantValidationResults, livenessValidationResults, imageUrl, imagePreviewLoaded]);    

    useEffect(() => {
        if (validationInitialized) {
            performValidation();
        }
    }, [validationInitialized]);    

    useEffect(() => {
        if (validationFinished) {
            Logger.info(`[CaptureValidationComponent] Result from validation ${JSON.stringify(captureStepValidationResults)}`);
            const validationEndTime = window.performance.now();
            Logger.info(`[CaptureValidationComponent] All validations ran in ${(validationEndTime - validationStartTime) / 1000 / 1000} seconds`);
            Logger.info('[CaptureValidationComponent] *************** CAPTURE VALIDATION COMPLETE ***************');
        }
    }, [validationFinished, captureStepValidationResults]);

    const getValidationResultForType = (type: ValidationTypes) => {
        return captureStepValidationResults[type];
    };

    const validationTitle = content.validation.validationTitle;
    const validationLoadingText = content.validation.validationLoadingText;

    return (
        <div id="capture-validation-results" className={classNames(styles.qualityChecksContainer, "capture-validation-results")}>
            <h2>{validationTitle}</h2>
            {!validationFinished && (
                <h3>
                    <FontAwesomeIcon className={classNames(styles.cogIcon, "fa-spin")} icon={faCog} />
                    {validationLoadingText}
                </h3>
            )}
            {validationFinished && Object.keys(captureStepValidationResults).map((validationType: ValidationTypes) => (
                <QualityCheck key={`quality-check-${validationType}`} validationResult={getValidationResultForType(validationType)} />
            ))}
        </div>
    );
};